import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Upload, X, Image as ImageIcon, Loader2, AlertCircle } from 'lucide-react';
import { uploadImage } from '../services/api';
import { toast } from '../hooks/use-toast';

const ImageUpload = ({ 
  onUploadComplete, 
  currentImage = null,
  onRemove = null,
  className = '',
  label = 'Upload Image',
  accept = 'image/*',
  maxSizeMB = 10
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(currentImage);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Compress image using Canvas API
  const compressImage = (file, maxWidth = 1920, quality = 0.82) => {
    return new Promise((resolve) => {
      // Skip compression for small files (<500KB) or non-raster formats
      if (file.size < 512000 || file.type === 'image/gif' || file.type === 'image/svg+xml') {
        resolve(file);
        return;
      }

      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        
        // Scale down if wider than maxWidth
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob && blob.size < file.size) {
              resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
            } else {
              resolve(file); // Keep original if compression didn't help
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      toast({
        title: 'Invalid File',
        description: 'Please select an image file (JPEG, PNG, GIF, WebP)',
        variant: 'destructive'
      });
      return;
    }

    // Validate file size
    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      setError(`File size must be less than ${maxSizeMB}MB`);
      toast({
        title: 'File Too Large',
        description: `Please select an image smaller than ${maxSizeMB}MB`,
        variant: 'destructive'
      });
      return;
    }

    setError(null);
    setUploading(true);
    setProgress(0);

    // Create local preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    try {
      // Compress image before upload
      const compressed = await compressImage(file);
      const result = await uploadImage(compressed, (percent) => {
        setProgress(percent);
      });

      if (result.success && result.url) {
        setPreview(result.url);
        onUploadComplete?.(result.url, result);
        toast({
          title: 'Upload Complete',
          description: 'Image uploaded successfully'
        });
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      setError('Failed to upload image');
      setPreview(currentImage); // Revert to original
      toast({
        title: 'Upload Failed',
        description: err.response?.data?.detail || 'Failed to upload image. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setError(null);
    onRemove?.();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && fileInputRef.current) {
      // Create a DataTransfer to set files
      const dt = new DataTransfer();
      dt.items.add(file);
      fileInputRef.current.files = dt.files;
      handleFileSelect({ target: { files: dt.files } });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept={accept}
        className="hidden"
        disabled={uploading}
      />

      {preview ? (
        <div className="relative group">
          <div className="relative rounded-lg overflow-hidden border-2 border-amber-900/30 bg-gray-900">
            <img
              src={preview}
              alt="Preview"
              className="w-full max-h-96 object-contain bg-gray-950"
            />
            {uploading && (
              <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center">
                <Loader2 className="w-8 h-8 text-amber-500 animate-spin mb-2" />
                <Progress value={progress} className="w-3/4 h-2" />
                <span className="text-amber-500 text-sm mt-2 font-typewriter">{progress}%</span>
              </div>
            )}
          </div>
          {!uploading && (
            <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                className="bg-gray-800/90 hover:bg-gray-700 text-white"
              >
                <Upload className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={handleRemove}
                className="bg-red-900/90 hover:bg-red-800"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div
          onClick={() => !uploading && fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
            transition-all duration-200
            ${uploading 
              ? 'border-amber-600 bg-amber-900/10' 
              : 'border-amber-900/50 hover:border-amber-600 hover:bg-amber-900/10'
            }
            ${error ? 'border-red-500' : ''}
          `}
        >
          {uploading ? (
            <div className="space-y-4">
              <Loader2 className="w-12 h-12 text-amber-500 mx-auto animate-spin" />
              <Progress value={progress} className="w-full h-2" />
              <p className="text-amber-500 font-typewriter">Uploading... {progress}%</p>
            </div>
          ) : (
            <>
              <ImageIcon className="w-12 h-12 text-amber-700 mx-auto mb-4" />
              <p className="text-gray-400 font-typewriter mb-2">{label}</p>
              <p className="text-gray-500 text-sm font-typewriter">
                Drag & drop or click to browse
              </p>
              <p className="text-gray-600 text-xs font-typewriter mt-2">
                Max size: {maxSizeMB}MB (JPEG, PNG, GIF, WebP)
              </p>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-red-500 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span className="font-typewriter">{error}</span>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
